import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader, Table } from "semantic-ui-react";
import { useGetIngredientPackaging, useGetProductsByIngredient } from "../../../../api/ingredients";
import {
  useGetCountries,
  useGetProduceTypes,
  useGetSpecifyIngredient
} from "../../../../api/static-data";
import { CommonTable } from "../../../../components/commonTable/CommonTable";
import { NewImageUploader } from "../../../../components/imageUploaderNew/ImageUploaderNew";
import { InputText } from "../../../../components/InputText/InputText";
import { InputTextArea } from "../../../../components/InputText/InputTextArea";
import { TitleView } from "../../../../components/titleView/TitleView";
import {
  IngredientDataType,
  IngredientDataTypeLabel,
  IngredientType
} from "../../../../config/enums/IngredientType.enum";
import { IngredientViewProductCol } from "../../../../config/tableColConstant";
import { useIngredientFlags } from "../../../../hooks/useIngredientFlags";
import IngredientPackagingView from "../../ingredientRegister/IngredientPackageing";
import SupplerIngredientDataView from "../../ingredientRegister/SupplierIngredientDataView";
import SupplierIngredientLcaDataView from "../../shared/components/SupplierIngredientLcaDataView";
import TransportationDetails from "../../transportationDetails/TransportationDetails";

const IngredientSummaryTab = ({ ingredientDetails }: any) => {
  const [viewEnvironmentalData, setViewEnvironmentalData] = useState<boolean>(false);
  const ingredientId = useMemo(() => ingredientDetails?.ingredient_id, [ingredientDetails]);
  const supplierId = useMemo(() => ingredientDetails?.supplier_id, [ingredientDetails]);

  const ingredientType = useMemo(
    () => ingredientDetails?.supplierIngredientDetails?.ingredient_type,
    [ingredientDetails]
  );
  const dataType = useMemo(
    () => ingredientDetails?.supplierIngredientDetails?.data_type,
    [ingredientDetails]
  );

  const { processIngredient, enableIngredient, lcaIngredient } = useIngredientFlags(
    ingredientType,
    dataType
  );

  let getPackagingData = {
    ingredientId: ingredientId,
    supplierId: supplierId,
    loadPackageData: true
  };

  let getProductsByIngredient = {
    ingredientId: ingredientId,
    supplierId: supplierId
  };

  const {
    register,
    setValue,
    formState: { errors },
    control
  } = useForm();

  const { data: ingredientPackageData, isLoading: loadingingredientPackageData } =
    useGetIngredientPackaging(getPackagingData);

  const { data: productsByIngredient, isLoading: loadingProductbyIngredient } =
    useGetProductsByIngredient(getProductsByIngredient);

  const packageDataView = useMemo(() => (enableIngredient ? true : false), [ingredientDetails]);
  const transportDataView = useMemo(() => (processIngredient ? true : false), [ingredientDetails]);
  const lcaDataView = useMemo(() => (lcaIngredient ? true : false), [ingredientDetails]);

  const enablePackageData = useMemo(() => {
    return packageDataView;
  }, [packageDataView]);

  const enableTransportData = useMemo(() => {
    const enableTransportDetails = transportDataView;
    return enableTransportDetails || (!enableTransportDetails && packageDataView);
  }, [transportDataView, packageDataView]);

  const enableLCAData = useMemo(() => {
    return lcaDataView;
  }, [lcaDataView]);

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const countries = () => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id.toString()
      }));
    }
  };

  const {
    data: produceTypesData,
    isSuccess: isProduceTypesSuccess,
    isLoading: isProduceTypesLoading
  } = useGetProduceTypes();

  const produceTypes = useMemo(() => {
    if (isProduceTypesSuccess) {
      return produceTypesData?.map((p: any) => ({
        text: p.produce_name,
        key: p.id,
        value: p.id
      }));
    }
  }, [produceTypesData, isProduceTypesSuccess]);

  const checkRawOrProcess = useMemo(() => {
    const data = {
      processIngredient: false,
      produceIngredient: false,
      enableIngredient: false,
      lcaIngredient: false
    };

    const isGeneric = dataType === IngredientDataType.GENERIC;
    const isLCA = dataType === IngredientDataType.CALCULATED_LCA;

    if (ingredientType === IngredientType.PROCESSED && isGeneric) {
      data.processIngredient = true;
    } else if (ingredientType === IngredientType.RAW && isGeneric) {
      data.produceIngredient = true;
      data.enableIngredient = true;
    } else if (
      (ingredientType === IngredientType.RAW || ingredientType === IngredientType.PROCESSED) &&
      isLCA
    ) {
      data.lcaIngredient = true;
    }
    return data;
  }, [ingredientType, dataType]);

  const { data: specifyIngredient } = useGetSpecifyIngredient();

  useEffect(() => {
    setValue("unit_size", ingredientDetails.supplierIngredientDetails?.unit_size || 0);
    setValue("ean_bar_code", ingredientDetails.supplierIngredientDetails?.ean_bar_code || 0);
    setValue("produceCultivar", ingredientDetails.supplierIngredientDetails?.produceCultivar || 0);

    if (dataType) {
      setViewEnvironmentalData(true);
    }
  }, [ingredientDetails]);

  const loadProductTableData = () => {
    return productsByIngredient?.map((data: any, index: any) => {
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>{data["productName"]}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data["salesUnitName"]}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data["saleUnitSize"]}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data["salesUnitType"]}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data["salesUnitEAN"]}</p>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  if (loadingingredientPackageData || loadingProductbyIngredient) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <Grid>
        <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
          <InputText
            labelName={"Ingredient Name"}
            placeholder="Ingredient Name"
            name="ingredient_name"
            defaultValues={ingredientDetails?.ingredient_name}
            disabled={true}
            customText={"removeInputBorder"}
          />
        </Grid.Column>
        <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
          <InputText
            labelName={"Ingredient Type"}
            placeholder="Ingredient Type"
            name="ingredient_type"
            defaultValues={ingredientType}
            disabled={true}
            customText={"removeInputBorder"}
          />
        </Grid.Column>
        <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
          <InputText
            labelName={"Data Type"}
            placeholder="Data Type"
            name="data_type"
            defaultValues={
              IngredientDataTypeLabel[dataType as keyof typeof IngredientDataTypeLabel] || ""
            }
            disabled={true}
            customText={"removeInputBorder"}
          />
        </Grid.Column>
        <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
          {!isEmpty(ingredientDetails?.ingredient_image_path) ? (
            <NewImageUploader
              defaultImage={ingredientDetails?.ingredient_image_path}
              uploadButtonView={false}
              deleteButtonView={false}
              mainTitle={"Ingredient Image"}
            />
          ) : null}
        </Grid.Column>
        <Grid.Column computer={16} tablet={16} mobile={16} className="paddingRemoveBottom">
          <InputTextArea
            labelName={"Other Info"}
            placeholder="Other Info"
            name="ingredient_other_info"
            defaultValues={ingredientDetails?.ingredient_other_info}
            disabled={true}
            customText={"removeInputBorder"}
          />
        </Grid.Column>
        <Grid.Column computer={16} tablet={16} mobile={16} className="productRegisterTable">
          <SupplerIngredientDataView
            dataView={true}
            register={register}
            control={control}
            errors={errors}
            supplierAsUser={ingredientDetails?.supplierAsUser}
            ingredientType={ingredientType}
            dataType={dataType}
            country={ingredientDetails?.supplierIngredientDetails?.countryOrigin}
            countries={() => countries()}
            isCountriesDataLoading={isCountriesDataLoading}
            defaultType={ingredientDetails?.supplierIngredientDetails?.unit_type}
            produceType={ingredientDetails?.supplierIngredientDetails?.produceType}
            produceTypes={produceTypes}
            isProduceTypesLoading={isProduceTypesLoading}
            farmingType={ingredientDetails?.supplierIngredientDetails?.farmingType || "Unknown"}
            fieldType={ingredientDetails?.supplierIngredientDetails?.fieldType || "Unknown"}
            disableEdit={true}
            checkRawOrProcess={checkRawOrProcess}
            specifyIngredient={specifyIngredient}
            specifyIngredientValue={ingredientDetails?.supplierIngredientDetails?.specifyIngredient}
            disable={true}
            setViewEnvironmentalData={setViewEnvironmentalData}
            viewEnvironmentalData={viewEnvironmentalData}
          />
        </Grid.Column>

        {enableLCAData ? (
          <>
            <SupplierIngredientLcaDataView
              dataView={true}
              ingredientId={ingredientId}
              supplierId={supplierId}
            />
          </>
        ) : null}

        {productsByIngredient?.length > 0 ? (
          <Grid.Column computer={16} tablet={16} mobile={16} className="productRegisterTable">
            <TitleView title="Ingredient Related Products" />
            <CommonTable tableHeaderData={IngredientViewProductCol}>
              {loadProductTableData()}
            </CommonTable>
          </Grid.Column>
        ) : null}

        {enablePackageData ? (
          <Grid.Column computer={16} tablet={16} mobile={16} className="paddingRemoveBottom">
            <IngredientPackagingView
              ingredientId={ingredientId}
              supplierId={supplierId}
              ingredientPackageData={ingredientPackageData}
              dataView
            />
          </Grid.Column>
        ) : null}

        {enableTransportData ? (
          <Grid.Column computer={16} tablet={16} mobile={16} className="paddingRemoveBottom">
            <TransportationDetails
              ingredientId={ingredientId}
              supplierId={supplierId}
              disabled={true}
            />
          </Grid.Column>
        ) : null}
      </Grid>
    </>
  );
};

export default IngredientSummaryTab;
